import { useState, useEffect } from 'react';
import { services } from '../../api';
import { QuizDetails } from '../../interfaces/api/quiz';

/**
 * Custom hook to fetch and manage quiz overview data based on quiz type.
 *
 * @param {number} id - The quiz identifier to fetch details for
 * @param {boolean} finalQuiz - Flag to determine quiz type (final quiz or section quiz)
 * @returns {{
 *   quizOverview: QuizDetails | null,
 *   loading: boolean
 * }} The state and quiz data.
 *
 * @example
 * const { quizOverview, loading } = useQuizOverview(123, true);
 * if (loading) return <LoadingSpinner />;
 * return (
 *   <QuizContent
 *     quizData={quizOverview}
 *   />
 * );
 */
export const useQuizOverview = (
  id: number,
  finalQuiz: boolean,
): {
  quizOverview: QuizDetails | null;
  loading: boolean;
  error: boolean;
} => {
  const [quizOverview, setQuizOverview] = useState<QuizDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  /**
   * Fetches quiz details from the API based on quiz type.
   * Updates the quizOverview state with the fetched data.
   *
   * @returns {Promise<void>}
   */
  useEffect(() => {
    const getDetails = async () => {
      setError(false);
      setLoading(true);
      try {
        const details = finalQuiz ? services.quiz.getFinalQuizDetails : services.quiz.getSectionQuizDetails;
        const res = await details(id);
        setQuizOverview(res.content || null);
      } catch (error) {
        setError(true);
        console.error('Error fetching course details:', JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    };

    getDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return {
    quizOverview,
    loading,
    error,
  };
};
